import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { User, Tenant, Restaurant, AppContextType } from '../types';
import apiService from '../services/api';

const AuthContext = createContext<AppContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [currentTenant, setCurrentTenant] = useState<Tenant | undefined>(undefined);
  const [currentRestaurant, setCurrentRestaurant] = useState<Restaurant | undefined>(undefined);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          const userData = await apiService.getCurrentUser();
          setUser(userData);
          setIsAuthenticated(true);

          // Use /tenant/current — works for all roles (no 403)
          if (userData.tenantId) {
            try {
              const tenant = await apiService.getCurrentTenant();
              setCurrentTenant(tenant);
            } catch {
              setCurrentTenant({
                id: userData.tenantId,
                name: 'Restaurant Tenant',
                subdomain: '',
                isActive: true,
                createdAt: new Date(),
                updatedAt: new Date()
              });
            }
          }

          // Load restaurant — falls back to placeholder on 403/404
          if (userData.restaurantId) {
            try {
              const restaurant = await apiService.getRestaurantById(userData.restaurantId);
              setCurrentRestaurant(restaurant);
            } catch {
              setCurrentRestaurant({
                id: userData.restaurantId,
                tenantId: userData.tenantId,
                name: 'Restaurant',
                address: '',
                phone: '',
                email: '',
                isActive: true,
                createdAt: new Date(),
                updatedAt: new Date()
              });
            }
          }
        } catch (error) {
          console.error('Failed to validate token:', error);
          localStorage.removeItem('auth_token');
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (restaurantCode: string, username: string, password: string, rememberRestaurantCode?: boolean): Promise<boolean> => {
    try {
      console.log('AuthContext.login called with:', { restaurantCode, username, rememberRestaurantCode });
      setIsLoading(true);
      const response = await apiService.login({
        restaurantCode,
        username,
        password,
        rememberRestaurantCode
      });
      console.log('AuthContext.login successful, response:', response);
      setUser(response.user);
      setIsAuthenticated(true);
      
      // Load tenant if available
      if (response.user.tenantId) {
        try {
          console.log('Loading tenant with ID:', response.user.tenantId);
          const tenant = await apiService.getTenantById(response.user.tenantId);
          console.log('Tenant loaded:', tenant);
          setCurrentTenant(tenant);
        } catch (error) {
          console.error('Failed to load tenant after login:', error);
        }
      }
      
      // Load restaurant if available
      if (response.user.restaurantId) {
        try {
          console.log('Loading restaurant with ID:', response.user.restaurantId);
          const restaurant = await apiService.getRestaurantById(response.user.restaurantId);
          console.log('Restaurant loaded:', restaurant);
          setCurrentRestaurant(restaurant);
        } catch (error) {
          console.error('Failed to load restaurant after login:', error);
        }
      }
      
      setIsLoading(false);
      console.log('AuthContext.login returning true');
      return true;
    } catch (error: any) {
      console.error('Login failed:', error);
      setIsLoading(false);
      console.log('AuthContext.login returning false');
      
      // Check if it's a network error
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
        console.warn('Network error - offline mode active');
        setError('Cannot connect to server. Please check your internet connection.');
      } else if (error.response?.status === 401) {
        console.warn('Authentication failed - invalid credentials');
        setError('Invalid credentials. Please check your restaurant code, username, and password.');
      } else {
        setError(error.message || 'Login failed. Please try again.');
      }
      
      return false;
    }
  };

  const logout = () => {
    apiService.logout().catch(console.error);
    localStorage.removeItem('cached_plan_features');
    setUser(undefined);
    setCurrentTenant(undefined);
    setCurrentRestaurant(undefined);
    setIsAuthenticated(false);
  };

  const switchTenant = async (tenantId: string): Promise<void> => {
    try {
      setIsLoading(true);
      // Fetch tenant details from API
      const tenant = await apiService.getTenantById(tenantId);
      setCurrentTenant(tenant);
      
      // Clear current restaurant when switching tenants
      setCurrentRestaurant(undefined);
      localStorage.removeItem('cached_plan_features');
      
      console.log('Switched to tenant:', tenant.name);
    } catch (error) {
      console.error('Failed to switch tenant:', error);
      // Fallback to demo tenant
      setCurrentTenant({
        id: tenantId,
        name: 'Switched Tenant',
        subdomain: 'switched.local',
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date()
      });
    } finally {
      setIsLoading(false);
    }
  };

  const switchRestaurant = async (restaurantId: string): Promise<void> => {
    try {
      setIsLoading(true);
      const restaurant = await apiService.getRestaurantById(restaurantId);
      setCurrentRestaurant(restaurant);
      localStorage.removeItem('cached_plan_features');
      console.log('Switched to restaurant:', restaurant.name);
    } catch (error) {
      console.error('Failed to switch restaurant:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCurrentTenant = async (): Promise<void> => {
    try {
      if (user?.tenantId) {
        const tenant = await apiService.getCurrentTenant();
        setCurrentTenant(tenant);
      }
    } catch (error) {
      console.error('Failed to load current tenant:', error);
    }
  };

  // Load tenant when user changes
  useEffect(() => {
    if (user && !currentTenant) {
      loadCurrentTenant();
    }
  }, [user, currentTenant]);

  const updateUser = async (data: { firstName?: string; lastName?: string; email?: string; username?: string; phone?: string }): Promise<void> => {
    if (!user) throw new Error('No user logged in');
    const updated = await apiService.updateUser(user.id, data);
    setUser(updated);
  };

  const value: AppContextType = {
    currentTenant,
    currentRestaurant,
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    switchTenant,
    switchRestaurant,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AppContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};